<template>
  <v-container class="d-flex flex-column h-full">
    <v-card color="darknavyblue" elevation="5" class="pa-4">
      <v-card-title>List Attachments</v-card-title>
      <v-card-text>
        <v-text-field
          v-model="listUrl"
          label="Application or Policy URL"
          prepend-icon="mdi-link-variant"
          hint="Paste a URL here for the object you want to list attachments for"
          persistent-hint
          :disabled="listLoading"
        />
        <div class="ml-10">
          <v-btn
            color="primary"
            class="mt-4"
            :disabled="listDisabled"
            :loading="listLoading"
            @click="handleList"
          >
            List Attachments
          </v-btn>
          <v-messages
            v-if="listError"
            class="mt-4"
            color="error"
            v-text="listError"
          />
        </div>
        <v-alert
          v-if="environmentMismatch"
          class="mt-4"
          type="warning"
          variant="tonal"
        >
          The URL belongs to a different environment than the selected environment.
          Select the matching environment to regenerate attachments.
        </v-alert>
        <v-data-table
          v-if="attachments.length"
          class="mt-4"
          :items="attachments"
          :loading="listLoading"
          :headers="[
            { title: 'ID', key: 'id' },
            { title: 'Name', key: 'name' },
            { title: 'Status', key: 'status' },
            { title: 'Actions', key: 'actions', align: 'center' },
          ]"
        >
          <template #item.actions="{ item }">
            <div class="d-flex justify-center py-4">
              <v-tooltip v-if="item.status === 'ATTACHMENT_STATUS_FAILED'" location="left">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    variant="flat"
                    size="32"
                    color="primary"
                    icon="mdi-refresh"
                    :disabled="regenerateLoading || environmentMismatch"
                    @click="copyAndRegenerate(item.id)"
                  />
                </template>
                <span>Trigger regenerate for this attachment id</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-card color="darknavyblue" elevation="5" class="pa-4 mt-4">
      <v-card-title>Regenerate Attachment</v-card-title>
      <v-card-text>
        <v-text-field
          v-model="regenerateId"
          label="Attachment ID"
          prepend-icon="mdi-counter"
          hint="The ID of the attachment you want to regenerate (must exist in the current Environment)"
          persistent-hint
        />
        <div class="ml-10">
          <v-btn
            color="primary"
            class="mt-4"
            :disabled="regenerateDisabled"
            :loading="regenerateLoading"
            @click="handleRegenerate"
          >
            Regenerate Attachment
          </v-btn>
          <v-messages
            v-if="regenerateError"
            key="regenerate-error"
            class="mt-4"
            color="error"
            v-text="regenerateError"
          />
          <v-messages
            v-if="regenerateSuccess"
            key="regenerate-success"
            class="mt-4"
            color="success"
            v-text="regenerateSuccess"
          />
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script lang="ts">
import { storeToRefs } from 'pinia'
import { computed, defineComponent, onBeforeUnmount, ref } from 'vue'

import { useApiStore } from '@/stores/api'
import { useEnvironmentStore } from '@/stores/environment'

import { parseUrl } from '@/utils/api'
import { bingoEnvironmentToInternalEnvironment } from '@/utils/environment'

import type { Environment, ListAttachmentsResponse_Attachment } from '@policyfly/protobuf/pfinternal'

const REGENERATE_POLL_INTERVAL = 10_000
const REGENERATE_POLL_TIMEOUT = 60_000

export default defineComponent({
  name: 'Attachments',

  setup () {
    const apiStore = useApiStore()
    const { currentEnvironment } = storeToRefs(useEnvironmentStore())

    // list
    const listUrl = ref('')
    const listLoading = ref(false)
    const listDisabled = computed(() => {
      return !listUrl.value || listLoading.value
    })
    const listError = ref<string | null>(null)
    const attachments = ref<ListAttachmentsResponse_Attachment[]>([])
    const listEnvironment = ref<Environment | null>(null)
    const environmentMismatch = computed(() => {
      return listEnvironment.value !== null &&
        listEnvironment.value !== bingoEnvironmentToInternalEnvironment(currentEnvironment.value)
    })
    async function handleList (): Promise<void> {
      listError.value = null
      listLoading.value = true
      attachments.value = []
      listEnvironment.value = null
      const requestedUrl = listUrl.value
      try {
        const url = parseUrl(requestedUrl)
        console.log('Listing attachments from URL:', url)
        const response = await apiStore.listAttachments({
          url,
        })
        listEnvironment.value = response.targetEnvironment
        attachments.value = response.attachments
        if (!response.attachments.length) {
          listError.value = 'No attachments that can be regenerated exist for that object'
        }
      } catch (error) {
        listError.value = `List Failed: ${(error as Error).message}`
        console.error(error)
      } finally {
        listLoading.value = false
      }
    }
    async function copyAndRegenerate (id: string): Promise<void> {
      regenerateId.value = id
      await handleRegenerate()
    }

    // regenerate
    const regenerateId = ref('')
    const regenerateLoading = ref(false)
    const regenerateDisabled = computed(() => {
      return !regenerateId.value || regenerateLoading.value
    })
    const regenerateError = ref<string | null>(null)
    const regenerateSuccess = ref<string | null>(null)
    const regenerateInterval = ref<number | null>(null)
    let regenerateStartTime = 0

    function startRegenerateInterval (id: string, targetEnvironment: Environment): void {
      regenerateSuccess.value = `Regeneration requested for attachment ${id}. Waiting for it to finish...`
      regenerateStartTime = Date.now()
      let polling = false
      const interval = window.setInterval(async () => {
        if (polling) return
        polling = true
        try {
          await pollRegeneration(id, targetEnvironment, interval)
        } finally {
          polling = false
        }
      }, REGENERATE_POLL_INTERVAL)
      regenerateInterval.value = interval
    }

    function stopRegenerateInterval (): void {
      if (regenerateInterval.value) window.clearInterval(regenerateInterval.value)
      regenerateInterval.value = null
      regenerateLoading.value = false
    }
    onBeforeUnmount(() => {
      stopRegenerateInterval()
    })

    async function pollRegeneration (id: string, targetEnvironment: Environment, currentInterval: number): Promise<void> {
      try {
        const response = await apiStore.readAttachment({ id, targetEnvironment })
        // check if the interval is still valid before proceeding
        if (regenerateInterval.value !== currentInterval) return
        // attachment is done regenerating
        if (response.status === 'ATTACHMENT_STATUS_READY') {
          stopRegenerateInterval()
          regenerateSuccess.value = `Successfully regenerated attachment ${id}`
          if (listUrl.value) await handleList()
        // timeout reached
        } else if (Date.now() - regenerateStartTime >= REGENERATE_POLL_TIMEOUT) {
          throw new Error(`Timed out waiting for attachment ${id} to regenerate`)
        }
      } catch (error) {
        if (regenerateInterval.value !== currentInterval) return
        stopRegenerateInterval()
        regenerateSuccess.value = null
        regenerateError.value = `Regenerate Failed: ${(error as Error).message}`
        console.error(error)
      }
    }

    async function handleRegenerate (): Promise<void> {
      regenerateError.value = null
      regenerateSuccess.value = null
      regenerateLoading.value = true
      try {
        const id = regenerateId.value
        const targetEnvironment = bingoEnvironmentToInternalEnvironment(currentEnvironment.value)
        console.log('Regenerating attachment with id:', id)
        await apiStore.regenerateAttachment({
          id,
          targetEnvironment,
        })
        startRegenerateInterval(id, targetEnvironment)
      } catch (error) {
        regenerateSuccess.value = null
        regenerateError.value = `Regenerate Failed: ${(error as Error).message}`
        console.error(error)
        regenerateLoading.value = false
      }
    }

    return {
      // list
      attachments,
      environmentMismatch,
      listUrl,
      listLoading,
      listDisabled,
      listError,
      handleList,
      copyAndRegenerate,
      // regenerate
      regenerateId,
      regenerateInterval,
      regenerateLoading,
      regenerateDisabled,
      regenerateError,
      regenerateSuccess,
      handleRegenerate,
    }
  },
})
</script>
